import { ReactNode, useState } from "react";

import Filters from "components/Filters";
import FiltersContentWrapper from "components/Filters/ContentWrapper";
import FiltersSidebar from "components/Filters/Sidebar";
import FiltersSplit from "components/Filters/Split";
import {
  FilterItem,
  FiltersItemsOptionsMap,
  SavedFilterView,
  SortOption,
} from "components/Filters/types";
import { InfoCircle } from "components/icons/generated";
import PaginationIndicator from "components/PaginationIndicator";
import SearchInput from "components/SearchInput";
import Box from "ds/components/Box";
import ButtonIcon from "ds/components/ButtonIcon";
import Link from "ds/components/Link";
import { AnalyticsPagePolicy } from "hooks/useAnalytics/pages/policy";
import DesktopView from "components/Mobile/DesktopView";
import FiltersMobileTriggerButton from "components/Filters/MobileTriggerButton";
import ViewHeaderWrapper from "components/ViewHeader/Wrapper";
import PageInfoHeaderTitle from "ds/components/PageInfoHeader/Title";
import PageInfoHeaderWrapper from "ds/components/PageInfoHeader/Wrapper";
import PageInfoHeaderActions from "ds/components/PageInfoHeader/Actions";

import PoliciesFeatureGateCallout from "../../FeatureGateCallout";
import {
  FILTERS_ORDER_SETTINGS_KEY,
  FILTERS_TYPE,
  INITIAL_SORT_DIRECTION,
  INITIAL_SORT_OPTION,
} from "../constants";
import PoliciesTemplatesFiltersLayoutHeader from "./Header";

type PoliciesTemplatesFiltersLayoutProps = {
  children: ReactNode;
  renderedEntitiesCount: number;
  isPageEmpty: boolean;
  loadingIndication: boolean;
  filteredCount: number;
  openPolicyTypesDrawer: () => void;
  openPolicyInfoDrawer: () => void;
  filters: FilterItem[];
  filtersMap: FiltersItemsOptionsMap;
  sortOptions: SortOption[];
  filtersLoading: boolean;
  refetchActiveSections: (fields: string[]) => void;
};

const PoliciesTemplatesFiltersLayout = ({
  filters,
  filtersMap,
  sortOptions,
  filtersLoading,
  refetchActiveSections,
  children,
  renderedEntitiesCount,
  isPageEmpty,
  loadingIndication,
  filteredCount,
  openPolicyTypesDrawer,
  openPolicyInfoDrawer,
}: PoliciesTemplatesFiltersLayoutProps) => {
  const [currentSavedView, setCurrentSavedView] = useState<SavedFilterView | undefined>(undefined);

  return (
    <>
      <PageInfoHeaderWrapper>
        <DesktopView>
          <Box align="center" gap="medium">
            <PageInfoHeaderTitle title="Templates" />
            <ButtonIcon onPress={openPolicyInfoDrawer} icon={InfoCircle} variant="ghost">
              Open info drawer
            </ButtonIcon>
          </Box>
        </DesktopView>
        <PageInfoHeaderActions>
          <DesktopView>
            <Link onPress={openPolicyTypesDrawer} startIcon={InfoCircle} size="small">
              Policy types
            </Link>
          </DesktopView>
          {!isPageEmpty && (
            <ViewHeaderWrapper direction="row" align="center" fullWidth>
              <PaginationIndicator
                currentCount={renderedEntitiesCount}
                totalCount={filteredCount}
                loading={loadingIndication}
                minimumLoadingDuration={200}
              />
              <SearchInput
                placeholder="Search templates"
                filtersOrderSettingsKey={FILTERS_ORDER_SETTINGS_KEY}
              />
              <FiltersMobileTriggerButton />
            </ViewHeaderWrapper>
          )}
        </PageInfoHeaderActions>
      </PageInfoHeaderWrapper>
      <PoliciesFeatureGateCallout />
      <Filters
        filters={filters}
        filtersItemsOptionsMap={filtersMap}
        filtersLoading={filtersLoading}
        sortOptions={sortOptions}
        initialSortOption={INITIAL_SORT_OPTION}
        initialSortDirection={INITIAL_SORT_DIRECTION}
        pollActiveSections={refetchActiveSections}
        filtersOrderSettingsKey={FILTERS_ORDER_SETTINGS_KEY}
        filtersType={FILTERS_TYPE}
        currentSavedView={currentSavedView}
        setCurrentSavedView={setCurrentSavedView}
      >
        <FiltersSplit>
          <FiltersSidebar analyticsPage={AnalyticsPagePolicy.PoliciesTemplates} />

          <FiltersContentWrapper>
            {!isPageEmpty && <PoliciesTemplatesFiltersLayoutHeader />}

            {children}
          </FiltersContentWrapper>
        </FiltersSplit>
      </Filters>
    </>
  );
};

export default PoliciesTemplatesFiltersLayout;
